// SINGLE SOURCE OF TRUTH for pages: id → component, title, nav group, flags.
// Sidebar, routing, and guards all derive from this — never duplicate page lists elsewhere.
import type { ComponentType } from 'react';
import './registry.de.js';
import './registry.fr.js';
import DashboardPage from './DashboardPage.js';
import ClientsPage from './ClientsPage.js';
import EngagementPage from './EngagementPage.js';
import DocumentsPage from './DocumentsPage.js';
import RegisterPage from './RegisterPage.js';
import AgentPage from './AgentPage.js';
import DecksPage from './decks/DecksPage.js';
import ContractingPage from './ContractingPage.js';
import AcceptancePage from './AcceptancePage.js';
import PortalAdminPage from './PortalAdminPage.js';
import AdminPage from './AdminPage.js';
import ScopeDocumentPage from './ScopeDocumentPage.js';

export type PageId =
  | 'dashboard' | 'clients' | 'lifecycle' | 'documents' | 'register'
  | 'agent' | 'decks' | 'contracting' | 'scope-document' | 'acceptance' | 'portal-admin' | 'admin';

export interface PageDef {
  id: PageId;
  title: string;
  navGroup: 'Overview' | 'CRM' | 'Engagement' | 'System';
  component: ComponentType;
  adminOnly?: boolean;
  needsEngagement?: boolean;   // page operates on the currently selected engagement
}

export const PAGES: PageDef[] = [
  { id: 'dashboard',    title: 'Dashboard',    navGroup: 'Overview',   component: DashboardPage },
  { id: 'clients',      title: 'Clients',      navGroup: 'CRM',        component: ClientsPage },
  { id: 'lifecycle',    title: 'Lifecycle',    navGroup: 'Engagement', component: EngagementPage,   needsEngagement: true },
  { id: 'documents',    title: 'Documents',    navGroup: 'Engagement', component: DocumentsPage,    needsEngagement: true },
  { id: 'register',     title: 'Register',     navGroup: 'Engagement', component: RegisterPage,     needsEngagement: true },
  { id: 'agent',        title: 'Agent',        navGroup: 'Engagement', component: AgentPage,        needsEngagement: true },
  { id: 'decks',        title: 'Decks',        navGroup: 'Engagement', component: DecksPage,        needsEngagement: true },
  { id: 'contracting',  title: 'Contracting',  navGroup: 'Engagement', component: ContractingPage,  needsEngagement: true },
  { id: 'scope-document', title: 'Scope document', navGroup: 'Engagement', component: ScopeDocumentPage, needsEngagement: true },
  { id: 'acceptance',   title: 'Acceptance',   navGroup: 'Engagement', component: AcceptancePage,   needsEngagement: true },
  { id: 'portal-admin', title: 'Client Portal', navGroup: 'Engagement', component: PortalAdminPage, needsEngagement: true },
  { id: 'admin',        title: 'Admin',        navGroup: 'System',     component: AdminPage,        adminOnly: true },
];

export const DEFAULT_PAGE: PageId = 'dashboard';

// NOTE: one parameterized detail route exists on top of this registry: '#client?id=N'
// (handled in App.tsx). It renders ClientPage but resolves to page id 'clients', so the
// sidebar needs no entry for it and the Clients nav item stays active inside a client.

/**
 * Retired route ids kept alive as aliases. The nav label is the deep link an operator will
 * type or paste, so the id must match the label — but links already in notifications, docs
 * and browser history must keep working.
 */
const ALIASES: Record<string, PageId> = {
  engagement: 'lifecycle',
  // Proposals folded into the Agent tab (its 'proposals' sub-tab). ROUTES.proposals now emits
  // '#agent?tab=proposals&…'; this alias keeps bare '#proposals' links in old notifications and
  // browser history landing on the page that absorbed them.
  proposals: 'agent',
};

export function findPage(id: string): PageDef | undefined {
  const canonical = ALIASES[id] ?? id;
  return PAGES.find((p) => p.id === canonical);
}
